import React from 'react';
import { Dialog } from '../reusable';
import { ITEM_PROGRESS, formatDateTimeEs, getRequestStatusColor } from './planningDomain';
import { formatRequestDateEs } from './commercialRequestsUtils';

const Field = ({ label, value, isDarkMode }) => (
  <div style={{ marginBottom: 10 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: isDarkMode ? '#a1a1aa' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 14, color: isDarkMode ? '#f4f4f5' : '#0f172a', marginTop: 2, wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

const TcoatProductRequestDetailModal = ({ isOpen, onClose, request, isDarkMode = false, onEdit }) => {
  if (!request) return null;
  const items = Array.isArray(request.items) ? request.items : [];
  const statusColor = getRequestStatusColor(request.status);

  const body = (
    <div style={{ padding: '0 20px 20px', overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 12, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
            Solicitud T-COAT
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 20, fontWeight: 700, color: isDarkMode ? '#f4f4f5' : '#0f172a' }}>
            {request.request_code || '—'}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: isDarkMode ? '#d4d4d8' : '#475569' }}>
            {request.reference || 'Sin referencia'}
            {request.status ? (
              <span
                style={{
                  marginLeft: 8,
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  background: `${statusColor}22`,
                  color: statusColor,
                  border: `1px solid ${statusColor}55`,
                }}
              >
                {request.status}
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px 20px',
          marginBottom: 20,
        }}
      >
        <Field label="Origen" value={request.origin} isDarkMode={isDarkMode} />
        <Field label="Fecha límite" value={formatRequestDateEs(request.needed_by_date)} isDarkMode={isDarkMode} />
        <Field label="OP vinculada" value={request.op_code} isDarkMode={isDarkMode} />
        <Field label="Cotización" value={request.quotation_code} isDarkMode={isDarkMode} />
        <Field label="Venta" value={request.sale_code} isDarkMode={isDarkMode} />
        {request.finished_at ? (
          <Field label="Terminada el" value={formatDateTimeEs(request.finished_at)} isDarkMode={isDarkMode} />
        ) : null}
      </div>

      {request.notes ? (
        <div style={{ marginBottom: 20 }}>
          <Field label="Notas" value={request.notes} isDarkMode={isDarkMode} />
        </div>
      ) : null}

      <h4 style={{ margin: '0 0 10px', fontSize: 14, color: isDarkMode ? '#f4f4f5' : '#0f172a' }}>
        Productos solicitados ({items.length})
      </h4>
      <div style={{ overflowX: 'auto', border: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`, borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
          <thead>
            <tr style={{ background: isDarkMode ? '#18181b' : '#f8fafc' }}>
              {['Producto', 'Color', 'Cantidad', 'Avance'].map((label) => (
                <th
                  key={label}
                  style={{
                    textAlign: label === 'Cantidad' ? 'right' : 'left',
                    padding: '10px 12px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: isDarkMode ? '#a1a1aa' : '#64748b',
                    borderBottom: `1px solid ${isDarkMode ? '#3f3f46' : '#e2e8f0'}`,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: 20, textAlign: 'center', color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
                  Sin productos registrados.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id || `${item.product_code}-${item.product_name}`}>
                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${isDarkMode ? '#27272a' : '#f1f5f9'}` }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: isDarkMode ? '#f4f4f5' : '#0f172a' }}>
                      {item.product_name || '—'}
                    </div>
                    <div style={{ fontSize: 11, color: isDarkMode ? '#a1a1aa' : '#64748b' }}>
                      {item.product_code || '—'}
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', borderTop: `1px solid ${isDarkMode ? '#27272a' : '#f1f5f9'}` }}>
                    <div style={{ fontSize: 12, color: isDarkMode ? '#e4e4e7' : '#334155' }}>
                      {[item.color_code, item.color_name].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: '10px 12px',
                      borderTop: `1px solid ${isDarkMode ? '#27272a' : '#f1f5f9'}`,
                      textAlign: 'right',
                      fontSize: 12,
                      color: isDarkMode ? '#f4f4f5' : '#0f172a',
                      fontWeight: 600,
                    }}
                  >
                    {Number(item.quantity || 0).toLocaleString('es-CL')} {item.unit || ''}
                  </td>
                  <td
                    style={{
                      padding: '10px 12px',
                      borderTop: `1px solid ${isDarkMode ? '#27272a' : '#f1f5f9'}`,
                      fontSize: 12,
                      fontWeight: 600,
                      color: isDarkMode ? '#e4e4e7' : '#334155',
                    }}
                  >
                    <div>{String(item.progress_status || '').trim() || ITEM_PROGRESS.planned}</div>
                    {item.finished_at ? (
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: isDarkMode ? '#a1a1aa' : '#64748b',
                          marginTop: 2,
                        }}
                      >
                        {formatDateTimeEs(item.finished_at)}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Detalle solicitud T-COAT" isDarkMode={isDarkMode} size="lg">
      {body}
      {onEdit ? (
        <div style={{ padding: '0 20px 20px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => onEdit(request)}
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 8,
              border: 'none',
              background: '#d97706',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Editar
          </button>
        </div>
      ) : null}
    </Dialog>
  );
};

export default TcoatProductRequestDetailModal;
